import { CommonModule, DatePipe, DecimalPipe } from "@angular/common";
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  HttpClientModule,
} from "@angular/common/http";
import {
  InteractionType,
  LogLevel,
  PublicClientApplication,
} from "@azure/msal-browser";
import { NgModule, enableProdMode, ErrorHandler } from "@angular/core";
import { TranslateLoader, TranslateModule } from "@ngx-translate/core";
import { AppComponent } from "./app.component";
import { PermissionRedirectPlaceholderComponent } from "./_guards/permissionRedirect.guard";
import { AppRoutingModule } from "./app-routing.module";
import { ChartService } from "./services/chart.service";
import { CommonComponentsModule } from "./common-components/common-components.module";
import { CustomToastComponent } from "./shared/custom-toast/custom-toast.component";
import { DIALOG_R_DATA } from "./injector/dialog-data.token";
import { DashboardFooterComponent } from "./components/dashboard-footer/dashboard-footer.component";
import { DataAnalysisPageComponent } from "./components/dataAnalysis/dataAnalysisPage.component";
import { DateFormatService } from "./services/dateFormat.service";
import { DeviceDetailComponent } from "./components/device-detail/device-detail.component";
import { DeviceVersionListComponent } from "./components/deviceVersionList/deviceVersionList.component";
import { DialogModalsModule } from "./modals/dialog-modals.module";
import { DialogService } from "./services/dialog.service";
import { EndpointInfoComponent } from "./components/endpoint-info/endpoint-info.component";
import { EndpointsComponent } from "./components/endpoints/endpoints.component";
import { EndpointsTableComponent } from "./components/endpoints-table/endpoints-table.component";
import { FooterComponent } from "./components/footer/footer.component";
import { HomeComponent } from "./components/home/home.component";
import { LoaderService } from "./services/loader.service";
import { MsalModule } from "@azure/msal-angular";
import { NotificationListComponent } from "./components/notification-list/notification-list.component";
import { NotificationSettingsComponent } from "./components/notification-settings/notification-settings.component";
import { OverlayService } from "./services/overlay.service";
import { PageHeaderComponent } from "./components/pageHeader/pageHeader.component";
import { PaginationInfoComponent } from "./components/pagination-info/pagination-info.component";
import { PreSignupUserListComponent } from "./components/preSignup/preSignupUserList.component";
import { RealTimeMonitoringDashboardComponent } from "./components/real-time-monitoring-dashboard/real-time-monitoring-dashboard.component";
import { ReportBodyComponent } from "./components/reports/report-body/report-body.component";
import { ReportHeaderComponent } from "./components/reports/report-header/report-header.component";
import { ReportListComponent } from "./components/report-list/report-list.component";
import { ReportViewComponent } from "./components/report-view/report-view.component";
import { ReportsComponent } from "./components/reports/reports.component";
import { ReportChartComponent } from "./components/reports/report-chart/report-chart.component";
import { RouterOutlet } from "@angular/router";
import { SampleListComponent } from "./components/sample-list/sample-list.component";
import { SegListComponent } from "./components/seg-list/seg-list.component";
import { SensorComponent } from "./components/sensor/sensor.component";
import { ShadowConstraintListComponent } from "./components/shadowConstraintList/shadowConstraintList.component";
import { SharedModule } from "./shared/shared.module";
import { SidebarComponent } from "./components/sidebar/sidebar.component";
import { SignupComponent } from "./components/signup/signup.component";
import { TenantListComponent } from "./components/tenantList/tenantList.component";
import { TenantDialogComponent } from "./modals/tenant-dialog/tenant-dialog.component";
import { TenantSensorListComponent } from "./components/tenantSensorList/tenantSensorList.component";
import { TimeZoneService } from "./services/timeZone.service";
import { ToastService } from "./services/toast.service";
import { ToastrModule } from "ngx-toastr";
import { CacheInterceptor, TokenInterceptor } from "./_helpers";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";
import { UserListComponent } from "./components/user-list/user-list.component";
import { UserAccountSecurityPopupComponent } from "./components/user-list/user-account-security-popup/user-account-security-popup.component";
import { ZonesComponent } from "./components/zones/zones.component";
import { environment } from "src/environments/environment";
import { ResetPasswordComponent } from "./components/reset-password/reset-password.component";
import { InfoWindowComponent } from "./components/info-window/info-window.component";
import { ForgotPasswordComponent } from "./components/forgot-password/forgot-password.component";
import { LogoComponent } from "./components/logo/logo.component";
import { LoginComponent } from "./components/login/login.component";
import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { DateTimeAdapter, OWL_DATE_TIME_FORMATS, OwlDateTimeModule, OwlNativeDateTimeModule } from '@danielmoncada/angular-datetime-picker';
import { DeviceDetailsMapClustererComponent } from "./shared/map/device-details-map-clusterer/device-details-map-clusterer.component";
import { LocationListComponent } from "./components/location-list/location-list.component";
import { PpeListComponent } from "./components/ppe-list/ppe-list.component";
import { ControlsListComponent } from "./components/controls-list/controls-list.component";
import { TaskListComponent } from "./components/task-list/task-list.component";
import { CustomOwlDateAdapter } from "./services/custom-owl-date-adapter";
import { AlertResponseComponent } from "./components/alert-response/alert-response.component";
import { AlertResponsePreviewComponent } from "./components/alert-response-preview/alert-response-preview.component";
import { RolePermissionListComponent } from "./components/role-permission-list/role-permission-list.component";
import { RoleRailComponent } from "./components/role-rail/role-rail.component";
import { RolePermissionsTabComponent } from "./components/role-permissions-tab/role-permissions-tab.component";
import { RolePermissionMatrixComponent } from "./components/role-permission-matrix/role-permission-matrix.component";
import { RolePermissionSaveBarComponent } from "./components/role-permission-save-bar/role-permission-save-bar.component";
import { RolePermissionSaveResultBannerComponent } from "./components/role-permission-save-result-banner/role-permission-save-result-banner.component";
import { RiskAssessmentComponent } from "./components/risk-assessment/risk-assessment.component";
import { AgentsListComponent } from './components/agents-list/agents-list.component';
import { JurisdictionsListComponent } from './components/jurisdictions-list/jurisdictions-list.component';
import { DevicesTableComponent } from "./components/devices-table/devices-table.component";
import { LanguageSwitcherComponent } from './components/language-switcher/language-switcher.component';
import { SurveyListComponent } from './components/survey-list/survey-list.component';
import { AgentGroupListComponent } from "./components/agent-group-list/agent-group-list.component";
import { CalibrationEquipmentComponent } from './components/calibration-equipment/calibration-equipment.component';
import { SamplingEquipmentComponent } from './components/sampling-equipment/sampling-equipment.component';
import { SampleMediaComponent } from './components/sample-media/sample-media.component';
import { AnalyticalMethodComponent } from './components/analytical-method/analytical-method.component';
import { OrganizationsComponent } from './components/organizations/organizations.component';
import { SampleHistoryComponent } from './components/sample-history/sample-history.component';
import { OelTypeComponent } from './components/oel-type/oel-type.component';
import { JobDictionaryComponent } from './components/job-dictionary/job-dictionary.component';
import { JobDictionaryConfigDialogComponent } from './modals/job-dictionary-config-dialog/job-dictionary-config-dialog.component';
import { JobDictionaryDialogComponent } from './modals/job-dictionary-dialog/job-dictionary-dialog.component';
import { JobDictionaryHistoryDialogComponent } from './modals/job-dictionary-history-dialog/job-dictionary-history-dialog.component';
import { JobMappingComponent } from './components/job-mapping/job-mapping.component';
import { LabIntegrationsComponent } from './components/lab-integrations/lab-integrations.component';
import { LabRequisitionListComponent } from './components/lab-requisition-list/lab-requisition-list.component';
import { FileAttachmentListComponent } from './components/file-attachment-list/file-attachment-list.component';
import { AccessLogListComponent } from './components/access-log-list/access-log-list.component';
import { ParametersListComponent } from './components/parameters-list/parameters-list.component';
import * as Sentry from '@sentry/angular';
import { LabIntegrationDetailsComponent } from './components/lab-integration-details/lab-integration-details.component';
import { ShiftListComponent } from './components/shift-list/shift-list.component';
import { SampleTemplateListComponent } from './components/sample-template-list/sample-template-list.component';
import { SurveyTemplateListComponent } from './components/survey-template-list/survey-template-list.component';
import { TemplateKindToggleComponent } from './components/template-kind-toggle/template-kind-toggle.component';
import { TemplateListHostComponent } from './components/template-list-host/template-list-host.component';
import { SamplePlanningListComponent } from './components/sample-planning-list/sample-planning-list.component';
import { SamplePlanningDetailComponent } from './components/sample-planning-detail/sample-planning-detail.component';
import { ReportTemplateListComponent } from './components/report-template-list/report-template-list.component';
import { CorporateReportingComponent } from './components/corporate-reporting/corporate-reporting.component';
import { AngularResizeEventModule } from 'angular-resize-event-package';
import { GridChromeModule } from './shared/grid/core-grid/grid-chrome.module';

export function dateFormatFactory(dateFormatService: DateFormatService) {
  const { userDateFormat, userTimeFormat, userDateWithTimeFormat } = dateFormatService; // Default format
  return {
    parseInput: 'l LT',
    fullPickerInput: userDateWithTimeFormat,
    datePickerInput: userDateFormat,
    timePickerInput: userTimeFormat,
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY'
  }
}

if (environment.production) {
  enableProdMode();
}

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, "./assets/i18n/", ".json");
}

export function loggerCallback(logLevel: LogLevel, message: string) {
}

@NgModule({
  declarations: [
    AppComponent,
    PermissionRedirectPlaceholderComponent,
    EndpointsTableComponent,
    ZonesComponent,
    UserListComponent,
    SegListComponent,
    SampleListComponent,
    ReportsComponent,
    ReportViewComponent,
    ReportListComponent,
    ReportChartComponent,
    RealTimeMonitoringDashboardComponent,
    PaginationInfoComponent,
    NotificationListComponent,
    NotificationSettingsComponent,
    HomeComponent,
    EndpointsComponent,
    EndpointInfoComponent,
    DevicesTableComponent,
    DeviceDetailComponent,
    DashboardFooterComponent,
    ReportBodyComponent,
    ReportHeaderComponent,
    FooterComponent,
    TenantSensorListComponent,
    ShadowConstraintListComponent,
    DeviceVersionListComponent,
    SidebarComponent,
    PageHeaderComponent,
    PreSignupUserListComponent,
    DataAnalysisPageComponent,
    SensorComponent,
    SignupComponent,
    ResetPasswordComponent,
    InfoWindowComponent,
    ForgotPasswordComponent,
    TenantListComponent,
    LogoComponent,
    LoginComponent,
    DeviceDetailsMapClustererComponent,
    CustomToastComponent,
    LocationListComponent,
    PpeListComponent,
    TaskListComponent,
    RolePermissionListComponent,
    RiskAssessmentComponent,
    AgentsListComponent,
    AgentGroupListComponent,
    JurisdictionsListComponent,
    LanguageSwitcherComponent,
    SurveyListComponent,
    CalibrationEquipmentComponent,
    SamplingEquipmentComponent,
    SampleMediaComponent,
    AnalyticalMethodComponent,
    OrganizationsComponent,
    SampleHistoryComponent,
    OelTypeComponent,
    LabIntegrationsComponent,
    LabRequisitionListComponent,
    FileAttachmentListComponent,
    ControlsListComponent,
    LabIntegrationDetailsComponent,
    ShiftListComponent,
    ParametersListComponent,
    SampleTemplateListComponent,
    SamplePlanningListComponent,
    SamplePlanningDetailComponent,
    ReportTemplateListComponent,
    CorporateReportingComponent,
    AccessLogListComponent,
    JobDictionaryComponent,
    JobDictionaryConfigDialogComponent,
    JobDictionaryDialogComponent,
    JobDictionaryHistoryDialogComponent,
    JobMappingComponent,
    TenantDialogComponent,
    UserAccountSecurityPopupComponent,
    AlertResponseComponent,
    AlertResponsePreviewComponent,
    RoleRailComponent,
    RolePermissionsTabComponent,
    RolePermissionMatrixComponent,
    RolePermissionSaveBarComponent,
    RolePermissionSaveResultBannerComponent,
    TemplateListHostComponent,
    TemplateKindToggleComponent,
    SurveyTemplateListComponent,
  ],
  imports: [
    CommonModule,
    BrowserModule,
    BrowserAnimationsModule,
    SharedModule,
    DialogModalsModule,
    CommonComponentsModule,
    RouterOutlet,
    AppRoutingModule,
    HttpClientModule,
    OwlDateTimeModule,
    OwlNativeDateTimeModule,
    ToastrModule.forRoot({
      toastComponent: CustomToastComponent,
    }),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    MsalModule.forRoot(
      new PublicClientApplication({
        auth: {
          clientId: environment.microsoft.client_id, // Application (client) ID from the app registration
          authority: environment.microsoft.authority, // The Azure cloud instance and the app's sign-in audience (tenant ID, common, organizations, or consumers)
          redirectUri: environment.microsoft.redirectUri, // This is your redirect URI
        },
        cache: {
          cacheLocation: "localStorage",
        },
        system: {
          loggerOptions: {
            loggerCallback,
            logLevel: LogLevel.Verbose,
            piiLoggingEnabled: false,
          },
        },
      }),
      {
        interactionType: InteractionType.Popup,
        authRequest: {
          scopes: ["user.read"],
        },
      },
      {
        interactionType: InteractionType.Popup,
        protectedResourceMap: new Map([
          ["https://graph.microsoft.com/v1.0/me", ["user.read"]],
        ]),
      }
    ),
    AngularResizeEventModule,
    GridChromeModule,
  ],
  providers: [
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    LoaderService,
    [DatePipe],
    [DecimalPipe],
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CacheInterceptor,
      multi: true,
    },
    DateFormatService,
    ToastService,
    OverlayService,
    TimeZoneService,
    ChartService,
    {
      provide: DIALOG_R_DATA,
      useValue: [],
    },
    DialogService,
    {
      provide: OWL_DATE_TIME_FORMATS,
      useFactory: dateFormatFactory,
      deps: [DateFormatService]
    },
    {
      provide: DateTimeAdapter,
      useClass: CustomOwlDateAdapter
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule { }
